import { Alert, DialogActions, DialogContent, DialogTitle } from '@mui/material';
import { useAppTFunction } from '@retail/app/i18n';
import { ContainedButton, Dialog, TextButton } from '@shared/components';
import { ImportStateValidation, withDialogSuspenseErrorBoundary } from '@retail/components';
import { useCallback, useEffect, useMemo } from 'react';
import { SpreadSheetRow } from '@retail/products/types';
import { getImportedMarkupConditions } from '@retail/markup/utils';
import { UploadContainer, useImportItemsStore } from '@retail/products/components';
import { MarkupUploadDTO, UploadedMarkup } from '@retail/markup/types';
import { MarkupConditionsImportConfig } from './MarkupConditionsImportConfig';
import { useConditionActions } from '@retail/calculus/context';
import { ConditionsNew } from '@retail/calculus/types';

interface Props {
  open: boolean;
  onClose: () => void;
  mgSupplierId: number;
  currentConditions?: ConditionsNew[];
}

const importConfig = MarkupConditionsImportConfig.instance;

function UploadMarkupConditionsDialog({ mgSupplierId, open, currentConditions, onClose }: Props) {
  const t = useAppTFunction();

  const { createMultipleConditions } = useConditionActions();

  const {
    importedFile,
    importSpreadsheet,
    clearState: resetImportMarkupConditions,
    run,
  } = useImportItemsStore<MarkupUploadDTO>();

  const errorRows = useMemo(() => run?.rows.filter((row) => row.status === 'error'), [run?.rows]);

  const importedMarkups: UploadedMarkup[] | undefined = useMemo(
    () =>
      run?.rows
        .filter(({ status }) => status === 'ok')
        .map(({ values }) => ({ ...values, mgSupplierId })),
    [run?.rows, mgSupplierId]
  );

  const handleImportFile = (inputRows: SpreadSheetRow[], file: File) => {
    importSpreadsheet(inputRows, file, importConfig);
  };

  const handleUploadMarkupConditions = useCallback(
    async (validMarkups: UploadedMarkup[]) => {
      const importedMarkupConditions = getImportedMarkupConditions(validMarkups, currentConditions);

      createMultipleConditions(importedMarkupConditions).then(onClose);
    },
    [createMultipleConditions, currentConditions, onClose]
  );

  useEffect(() => {
    if (!open) resetImportMarkupConditions();
  }, [open, resetImportMarkupConditions]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ pb: 1 }}>{t('calculus.actions.uploadMarkup')}</DialogTitle>

      <DialogContent>
        <UploadContainer
          isLarge
          importedFile={importedFile}
          onDownloadTemplate={() => importConfig.downloadTemplateAsExcel('markup-template')}
          importSpreadsheet={handleImportFile}
          resetImportedStates={resetImportMarkupConditions}
          renderValidation={
            run &&
            (run?.summary.status === 'success' ? (
              <Alert severity="success" sx={{ display: 'flex', alignItems: 'center' }}>
                {t('products.uploadItems.nrItemsRead', {
                  count: run.summary.okCount,
                  totalCount: run.summary.totalCount,
                })}
              </Alert>
            ) : (
              <ImportStateValidation
                missingRequiredColumns={run.missingRequiredColumns}
                invalidDataRows={errorRows?.map((row) => ({
                  index: row.rowIndex,
                  errors: row.errors
                    .map((error) => ({
                      column: error.column,
                      reason: error.reason,
                      value: error.raw,
                    }))
                    .concat(
                      // @ts-ignore
                      (row.rowErrors ?? []).map(({ message }) => ({
                        reason: message,
                      }))
                    ),
                }))}
                noRows={run.rows.length === 0}
              />
            ))
          }
        />
      </DialogContent>

      <DialogActions>
        <TextButton onClick={onClose} size="small">
          {t('calculus.cancel')}
        </TextButton>
        <ContainedButton
          disabled={!!run?.missingRequiredColumns.length || !!errorRows?.length}
          onClick={
            importedMarkups ? () => handleUploadMarkupConditions(importedMarkups) : undefined
          }
          size="small"
          color="secondary"
        >
          {t('calculus.toApproval')}
        </ContainedButton>
      </DialogActions>
    </Dialog>
  );
}

export default withDialogSuspenseErrorBoundary(UploadMarkupConditionsDialog);
